








































































































































































import { Component, Mixins, Prop } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";
import SupportedLocaleCodes from "@/locales";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import LanguagesStore from "@/mixins/constants/languagesStore";

// Data modules
import { DataModules } from "@/store/modules/data";
import { IPWhitelistOptions, IPWhitelistTypes } from "@/data/constants";

// Types
import { IUser, IUserForm } from "@/models/users";

@Component({
  components: {
    "user-permissions": () =>
      import("@/components/app/admin/users/userPermissions.vue")
  }
})
export default class AddEditUserModal extends Mixins(
  RequestPromises,
  LanguagesStore,
  HasValidation
) {
  // Refs

  public declare $refs: {
    name: HTMLFormElement;
    observer: InstanceType<typeof ValidationObserver>;
  };

  // Props

  @Prop({ default: false })
  public isApiToken!: boolean;

  @Prop({ default: null })
  public user!: IUser;

  // Data

  public isProcessing = false;
  public form = {} as IUserForm;
  public initialForm = {} as IUserForm;

  public ipWhitelistOptions = IPWhitelistOptions(this);

  // Life-cycle methods

  public async created() {
    this.initiateForm();

    this.$nextTick(() => {
      if (this.$_.has(this.$refs, "name")) {
        this.$refs.name.focus();
      }
    });
  }

  // Methods

  public initiateForm() {
    this.form = this.isUpdate
      ? _.merge(
          { user_brand_group: [] },
          _.pick(
            this.user,
            _.concat(
              [
                "active",
                "admin",
                "ip_whitelist",
                "name",
                "user_brand_group",
                "whitelisted_ips"
              ],
              this.isApiToken
                ? []
                : ["email", "interface_language_code", "document_language_code"]
            )
          ) as IUserForm
        )
      : ({
          name: "",
          ip_whitelist: 0,
          ...(this.isApiToken
            ? { api_user: true }
            : {
                email: "",
                interface_language_code: SupportedLocaleCodes.EN,
                document_language_code: SupportedLocaleCodes.EN
              })
        } as IUserForm);

    this.initialForm = _.cloneDeep(this.form);
  }

  public async validateBeforeSubmit() {
    if (!this.canSendForm) return;

    try {
      this.isProcessing = true;

      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      const user = this.isUpdate
        ? await this.updateUser()
        : await this.createUser();

      this.$emit("success", user);
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public createUser(): Promise<IUser> {
    return this.$store.dispatch(`data/${DataModules.USERS}/create`, {
      data: this.form
    });
  }

  public updateUser(): Promise<IUser> {
    return this.$store.dispatch(`data/${DataModules.USERS}/update`, {
      id: this.user.id,
      data: this.newFormValues
    });
  }

  public manageWhitelistedIps(value) {
    if (value !== IPWhitelistTypes.SECURE) {
      this.form.whitelisted_ips = "";
    }
  }

  // Computed

  public get isUpdate() {
    return !_.isNull(this.user);
  }

  public get canCreate() {
    return this.$userCan("create_user");
  }

  public get canUpdate() {
    return this.$userCan("update_user");
  }

  public get canSendForm() {
    return this.isUpdate ? this.canUpdate : this.canCreate;
  }

  public get title() {
    if (this.isUpdate) {
      return this.isApiToken
        ? this.$t("_action.update_api_token")
        : this.$t("_action.update_staff_user");
    } else
      return this.isApiToken
        ? this.$t("_action.create_api_token")
        : this.$t("_action.create_staff_user");
  }

  public get newFormValues() {
    const newValues: any = this.$_.omitBy(this.form, (v, k) => {
      return this.initialForm[k] === v;
    });

    if (
      _.isEqual(this.form.user_brand_group, this.initialForm.user_brand_group)
    ) {
      delete newValues.user_brand_group;
    }

    return newValues;
  }

  public get formIsDisabled() {
    return (
      this.isProcessing ||
      this.$_.isEqual(this.form, this.initialForm) ||
      this.hasErrors ||
      !this.canSendForm
    );
  }
}
